import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import useAppContext from 'flow/hooks/useAppContext';
import { TranscriptionExtendedAction } from 'flow/types/CallRecord';
import SectionTitle from 'flow/components/pbx/v2/detail-page/SectionTitle';

interface Props {
    actions?: TranscriptionExtendedAction[];
    animate?: boolean;
    startDelayMs?: number;
}

const ITEM_STAGGER_MS = 110;

const TranscriptionActionPoints = ({
    actions,
    animate,
    startDelayMs = 0,
}: Props) => {
    const { i18next } = useAppContext();
    const [sectionVisible, setSectionVisible] = useState(!animate);
    const [visibleItemCount, setVisibleItemCount] = useState(
        animate ? 0 : (actions?.length ?? 0),
    );

    useEffect(() => {
        if (!animate || !actions?.length) {
            setSectionVisible(true);
            setVisibleItemCount(actions?.length ?? 0);
            return undefined;
        }
        setSectionVisible(false);
        setVisibleItemCount(0);
        const sectionTimer = window.setTimeout(
            () => setSectionVisible(true),
            startDelayMs,
        );
        const itemTimers = actions.map((_, i) =>
            window.setTimeout(
                () => setVisibleItemCount((v) => Math.max(v, i + 1)),
                startDelayMs + i * ITEM_STAGGER_MS,
            ),
        );
        return () => {
            window.clearTimeout(sectionTimer);
            itemTimers.forEach((id) => window.clearTimeout(id));
        };
    }, [animate, actions, startDelayMs]);

    if (!actions?.length) return null;

    // When not animating, derive visibility directly from props so a first render
    // with `actions` still undefined (pre-existing transcription whose DTO is being
    // fetched) can't leave the useState baseline at 0 and trigger a spurious fade
    // once the items arrive.
    const effectiveSectionVisible = animate ? sectionVisible : true;
    const effectiveVisibleItemCount = animate
        ? visibleItemCount
        : actions.length;

    return (
        <div
            className={twMerge(
                'flex flex-col',
                animate && 'transition-opacity duration-300 ease-out',
                effectiveSectionVisible ? 'opacity-100' : 'opacity-0',
            )}
        >
            <SectionTitle title={i18next.t('ai.transcript.actions')} />
            <ul className="text-sm antialiased">
                {actions.map((action, i) => (
                    <li
                        key={action.task}
                        className={twMerge(
                            animate && 'transition-all duration-300 ease-out',
                            i < effectiveVisibleItemCount
                                ? 'translate-y-0 opacity-100'
                                : 'translate-y-1 opacity-0',
                        )}
                    >
                        {'• '}
                        {action.task}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default TranscriptionActionPoints;
